import { ref } from 'vue';

import { useGlobalContentStore } from '~/store/globalContent';
import { useUiStore } from '~/store/ui';

const cachedResponses = {} as any;
const doNotCacheUrls = ['basket', 'checkout', 'payment', '/me', 'reviews', '/stock-alerts'];
const lastError = ref(null) as any;
const lastErrorCode = ref<number | null>(null);
const lastResponse = ref(null) as any;
const lastResponseCode = ref<number | null>(null);
const pending = ref(false);

type AdditionalHeaders = {
  [key: string]: string;
}

export default function useApiFetch() {
  const { $t } = useNuxtApp();
  const path = useRoute().path?.split('/').filter((str) => str.length).shift();

  const accountToken = useCookie('userToken', { maxAge: 3600 * 24 * 30 * 12, path: '/' + path });

  const runtimeConfig = useRuntimeConfig();

  const apiGet = async <T>(url: string, additionalHeaders?: null | AdditionalHeaders, fromCache = true): Promise<T> => {
    return req<T>(url, 'GET', null, additionalHeaders, fromCache);
  };

  const apiPost = async <T>(url: string, body?: null | object, additionalHeaders?: null | AdditionalHeaders, fromCache = false): Promise<T> => {
    return req<T>(url, 'POST', body, additionalHeaders, fromCache);
  };

  // fromCache defaults to false here, same as apiPost above - req()'s own
  // default is true, meant for apiGet, and the cache key is just the URL (+
  // body) with no HTTP method in it, so without this a mutating PUT/PATCH
  // could silently be served a stale cached response instead of ever
  // hitting the network (see apiDelete's own comment for the same issue,
  // found via a sample-request delete that stopped firing a request at all).
  const apiPut = async <T>(url: string, body?: null | object, additionalHeaders?: null | AdditionalHeaders, fromCache = false): Promise<T> => {
    return req<T>(url, 'PUT', body, additionalHeaders, fromCache);
  };

  const apiPatch = async <T>(url: string, body?: null | object, additionalHeaders?: null | AdditionalHeaders, fromCache = false): Promise<T> => {
    return req<T>(url, 'PATCH', body, additionalHeaders, fromCache);
  };

  const apiDelete = async <T>(url: string, body?: null | object, additionalHeaders?: null | AdditionalHeaders): Promise<T> => {
    // req()'s own default is fromCache = true, and the cache key is just the
    // URL (+ body) with no HTTP method in it - every other caller here that
    // shouldn't be cached (apiPost) already overrides that default; apiDelete
    // never did, so a delete could silently be served a cached response
    // (from an earlier GET/DELETE to the same URL) instead of ever hitting
    // the network - a destructive request must never be servable from cache.
    return req<T>(url, 'DELETE', body, additionalHeaders, false);
  };

  const req = async <T>(url: string, method: string, body?: null | object, additionalHeaders?: null | AdditionalHeaders, fromCache = true): Promise<T | any> => {
    pending.value = true;
    const uiStore = useUiStore();
    uiStore.clearFormErrorMsg();
    const cacheKey = url + (body ? JSON.stringify(body) : '');
    if (fromCache && process.client && cachedResponses[cacheKey]) {
      pending.value = false;

      return cachedResponses[cacheKey] as T;
    }
    lastResponse.value = null;
    lastResponseCode.value = null;
    lastError.value = null;
    lastErrorCode.value = null;
    const globalContent = useGlobalContentStore();
    try {
      const fullUrl = `${runtimeConfig.public.apiUrl}${url}`;
      if (globalContent.getEnvironment === 'local') {
        process.env['NODE_TLS_REJECT_UNAUTHORIZED'] = '0';
      }

      const res = await $fetch<T>(fullUrl, {
        async onRequest({ options }) {
          options.method = method;

          const headers = new Headers({
            Accept: 'application/json',
            HostName: globalContent.hostname ?? '',
            BasePath: globalContent.getApiApplicationBasePath ?? '',
            ApplicationId: `${globalContent.getApplicationId ?? ''}`,
            IsB2B: globalContent.getIsB2b ? 'true' : 'false',
          });

          const shouldAttachAuth = Boolean(accountToken.value) && !(additionalHeaders && 'Authorization' in additionalHeaders);
          if (shouldAttachAuth) {
            headers.set('Authorization', `Bearer ${accountToken.value}`);
          }

          if (additionalHeaders) {
            Object.entries(additionalHeaders).forEach(([key, value]) => {
              headers.set(key, value);
            });
          }

          options.headers = headers;

          if (body) {
            options.body = body instanceof FormData ? body : JSON.stringify(body);
          }
        },
        async onResponse({ response }) {
          if (!response.ok) {
            lastError.value = response._data;
            lastErrorCode.value = response.status || null;
            return null;
          }
          lastResponseCode.value = response.status || null;
          return response._data;
        },
      });

      if (process.client && !doNotCacheUrls.some(doNotCache => url.includes(doNotCache))) {
        cachedResponses[cacheKey] = res;
      }

      lastResponse.value = res;
      return res as T;

    } catch (e) {
      console.error(e);
      return null;
    } finally {
      pending.value = false;
    }
  };

  const handleApiError = (context?: string, forceFormError?: boolean, fadeOutErrorMessage?: boolean, forceGlobalError?: boolean) => {
    console.error('handleApiError');
    const uiStore = useUiStore();
    if (lastErrorCode.value === 403) {
      uiStore.setTemporaryError($t('apiError.unauthorized'), fadeOutErrorMessage ? 5000 : 0);
    } else if ((lastErrorCode.value === 422 || forceFormError) && !forceGlobalError) {
      uiStore.setFormErrorMsg(lastError.value, context);
    } else {
      if (lastError?.value?.message) {
        uiStore.setTemporaryError(lastError.value.message, fadeOutErrorMessage ? 5000 : 0);
      } else {
        uiStore.setTemporaryError($t('apiError.unknown'), fadeOutErrorMessage ? 5000 : 0);
      }
    }
  };

  return {
    apiGet,
    apiPost,
    apiPut,
    apiPatch,
    apiDelete,
    lastError,
    lastErrorCode,
    lastResponse,
    lastResponseCode,
    pending,
    accountToken,
    handleApiError,
  };
}
